import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "407",
  "slug": "why-farm-fields-are-circles-from-space",
  "title": "Why Are Farm Fields Perfect Circles From Space?",
  "excerpt": "Those giant green circles scattered across deserts and plains are not a mystery — they are one machine, drawing the same shape millions of times over. Here is the story of center-pivot irrigation.",
  "date": "2026-06-11",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1619719826894-89d6c4fd5739?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxpcnJpZ2F0aW9uJTIwZmFybSUyMGFlcmlhbHxlbnwxfDB8fHwxNzgxMTY1NTQyfDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "The perfect circles you see in farmland from space are made by center-pivot irrigation: a long sprinkler arm rotates around a central water pump, watering a circular area. Invented in the 1940s, these systems are common across the US High Plains, Saudi Arabia, Egypt, and Libya, and each circle is typically about 800 metres across.",
  "content": [
    {
      "type": "paragraph",
      "text": "Look at almost any farming region from a satellite view and you will eventually spot them: fields that are perfect green circles, often packed together like coins on a table, sometimes glowing in the middle of an otherwise empty desert. They look engineered, even alien. They are in fact the signature of one of the most quietly transformative machines in modern agriculture — and once you know what draws the circle, you will spot it everywhere."
    },
    {
      "type": "heading",
      "text": "One machine, one shape"
    },
    {
      "type": "paragraph",
      "text": "The circles are made by center-pivot irrigation. At the centre of each field sits a pump connected to a long arm of sprinkler pipe, sometimes hundreds of metres long, mounted on wheeled towers. The arm rotates slowly around the central pivot like the hand of a clock, spraying water as it goes. Because every point on the arm sweeps out a circle, the watered area is a circle too. The corners of the square plot of land simply stay dry — which is why, from above, you see a green disc inscribed in a paler square."
    },
    {
      "type": "heading",
      "text": "Where the idea came from"
    },
    {
      "type": "paragraph",
      "text": "Center-pivot irrigation was developed in the United States in the 1940s by a farmer named Frank Zybach, who patented a self-propelled sprinkler system in 1952. It solved a stubborn problem: watering large fields evenly without armies of workers moving pipes by hand. By automating the whole process, a single pivot could irrigate a big circular field on its own, and the technology spread across the American Great Plains and then around the world."
    },
    {
      "type": "heading",
      "text": "Why circles instead of squares"
    },
    {
      "type": "paragraph",
      "text": "It would be more land-efficient to water the whole square, so why settle for a circle? Because the rotating arm is dramatically simpler, cheaper, and more reliable than any machine that could cover a rectangle. A pivot needs only one fixed water source at the centre and one motion — rotation. Trying to water the corners requires extra folding arms and complex controls that often are not worth the cost. For most farmers, the lost corners are a fair price for a system that runs itself, so the circle won."
    },
    {
      "type": "list",
      "items": [
        "A typical center-pivot circle is around 800 metres in diameter, set inside a square quarter-section plot of 160 acres (about 65 hectares).",
        "The dry corners are sometimes used for storage, a second hardier crop, or simply left as scrub.",
        "Different colours across a cluster of circles usually mean different crops, or the same crop at different growth stages.",
        "A bare brown circle next to green ones is often a field that has just been harvested or is resting between plantings."
      ]
    },
    {
      "type": "heading",
      "text": "The desert circles"
    },
    {
      "type": "paragraph",
      "text": "The most dramatic examples are in places where there is no surface water at all. In the Wadi As-Sirhan basin of Saudi Arabia, in parts of Egypt and Libya, and across the US High Plains, fields of circles bloom in the middle of bare desert. They are watered by fossil groundwater pumped up from deep underground aquifers — water that fell as rain thousands of years ago. From orbit they are some of the most striking human-made patterns on the planet: lush green dots in a sea of sand, each one drawn by a single slowly turning arm."
    },
    {
      "type": "heading",
      "text": "A pattern with a hidden cost"
    },
    {
      "type": "paragraph",
      "text": "Those desert circles are also a visible record of a slow-moving problem. Much of the groundwater feeding them is fossil water that is not being replenished — once it is pumped out, it is effectively gone. Satellites have watched whole irrigation districts expand and then contract as aquifers run low: clusters of bright circles appear over a decade, then fade back to desert as the wells fail. The High Plains over the Ogallala Aquifer and the Saudi desert schemes are both textbook cases. So the circles are not only a neat visual quirk — they are a long-term diary of how a region is spending its water, written in a pattern you can only really read from above."
    }
  ]
};
